import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaHeart, FaComment, FaShare, FaBookmark, FaEllipsisH, FaPlay, FaLock, FaGlobe, FaTrash, FaSmile, FaTimes } from 'react-icons/fa';
import { likeLoveshot, unlikeLoveshot, saveLoveshot, unsaveLoveshot, deleteLoveshot, updateLoveshotPrivacy, addComment } from '../../../store/slices/loveshotsSlice';
import './LoveShotCard.css';

const emojis = ['😀', '😂', '😍', '🥰', '😘', '😉', '😎', '🤔', '😢', '😭', '😤', '😡', '🥺', '😴', '🤤', '🤗', '🤭', '🤫', '🤥', '😐', '😑', '😶', '🙄', '😏', '😣', '😥', '😮', '🤐', '😯', '😪', '😫', '🥱', '😌', '😛', '😜', '😝', '💕', '❤️', '💖', '💗', '🔥', '⭐', '✨', '🎉', '👍', '👏'];

const LoveShotCard = ({ loveshot, onDelete }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [showComments, setShowComments] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [comment, setComment] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isCommentLoading, setIsCommentLoading] = useState(false);
  const [showFullImage, setShowFullImage] = useState(false);
  const [showHeartAnimation, setShowHeartAnimation] = useState(false);
  const menuRef = useRef(null);
  const clickTimerRef = useRef(null);

  const isLiked = loveshot.likes?.some(like => like.user_id === user?.id);
  const isSaved = loveshot.saves?.some(save => save.user_id === user?.id);
  const isOwner = loveshot.user?.id === user?.id;
  const isPrivate = loveshot.is_hidden;

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const handleLike = () => {
    if (isLiked) {
      dispatch(unlikeLoveshot(loveshot.id));
    } else {
      dispatch(likeLoveshot(loveshot.id));
    }
  };

  const handleSave = () => {
    if (isSaved) {
      dispatch(unsaveLoveshot(loveshot.id));
    } else {
      dispatch(saveLoveshot(loveshot.id));
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (comment.trim() && !isCommentLoading) {
      setIsCommentLoading(true);
      try {
        await dispatch(addComment({ loveshotId: loveshot.id, content: comment })).unwrap();
        setComment('');
        setShowEmojiPicker(false);
      } catch (error) {
        console.error('Error adding comment:', error);
      } finally {
        setIsCommentLoading(false);
      }
    }
  };

  const handleTogglePrivacy = () => {
    dispatch(updateLoveshotPrivacy({
      id: loveshot.id,
      is_hidden: !isPrivate
    }));
    setShowMenu(false);
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await dispatch(deleteLoveshot(loveshot.id));
      if (onDelete) {
        onDelete(loveshot.id);
      }
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error('Error deleting loveshot:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setShowMenu(false);
  };

  const handleImageClick = () => {
    // Clear any pending single click timer
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    
    // Set a timer to distinguish between single and double click
    clickTimerRef.current = setTimeout(() => {
      setShowFullImage(true);
      clickTimerRef.current = null;
    }, 250);
  };

  const handleImageDoubleClick = (e) => {
    e.preventDefault();
    // Clear the single click timer
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    
    // Trigger like if not already liked
    if (!isLiked) {
      dispatch(likeLoveshot(loveshot.id));
    }
    
    // Show heart animation
    setShowHeartAnimation(true);
    setTimeout(() => {
      setShowHeartAnimation(false);
    }, 1000);
  };

  const handleCloseFullImage = () => {
    setShowFullImage(false);
  };

  return (
    <div className="loveshot-card">
      {/* Header */}
      <div className="loveshot-header">
        <div className="user-info">
          <img
            src={loveshot.user?.profile?.photo_url || '/default-avatar.svg'}
            alt={loveshot.user?.username}
            className="user-avatar"

          />
          <div className="user-details">
            <h4>{loveshot.user?.first_name} {loveshot.user?.last_name}</h4>
            <span className="username">@{loveshot.user?.username}</span>
          </div>
        </div>
        {isOwner && (
          <div className="options-menu-container" ref={menuRef}>
            <button 
              className="options-btn"
              onClick={() => setShowMenu(!showMenu)}
            >
              <FaEllipsisH />
            </button>
            {showMenu && (
              <div className="options-menu">
                <button 
                  className="menu-item privacy-toggle"
                  onClick={handleTogglePrivacy}
                >
                  {isPrivate ? <FaGlobe /> : <FaLock />}
                  <span>{isPrivate ? 'Rendre public' : 'Rendre privé'}</span>
                </button>
                <button 
                  className="menu-item delete"
                  onClick={handleDelete}
                >
                  <FaTrash />
                  <span>Supprimer</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Media */}
      <div className="loveshot-media" onClick={handleImageClick} onDoubleClick={handleImageDoubleClick}>
        {loveshot.media_type === 'video' ? (
          <video controls className="loveshot-video">
            <source src={loveshot.media_url} type="video/mp4" />
            <FaPlay className="play-icon" />
          </video>
        ) : (
          <>
            <img 
              src={loveshot.media_url} 
              alt="LoveShot" 
              className="loveshot-image"
              onError={(e) => {
                if (!e.target.src.includes('data:image')) {
                  console.error('Image failed to load:', loveshot.media_url);
                  // Use a simple SVG placeholder as base64
                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22400%22 height=%22400%22 viewBox=%220 0 400 400%22%3E%3Crect width=%22400%22 height=%22400%22 fill=%22%23f5f5f5%22/%3E%3Ccircle cx=%22200%22 cy=%22150%22 r=%2250%22 fill=%22%23e0e0e0%22/%3E%3Cpath d=%22M 100 300 L 150 200 L 250 280 L 300 220 L 350 300 L 350 400 L 50 400 Z%22 fill=%22%23e0e0e0%22/%3E%3Ctext x=%22200%22 y=%22380%22 font-size=%2214%22 fill=%22%23999%22 text-anchor=%22middle%22%3ENo image%3C/text%3E%3C/svg%3E';
                }
              }}
            />
            {showHeartAnimation && (
              <div className="heart-animation-overlay">
                <FaHeart className="animated-heart" />
              </div>
            )}
          </>
        )}
      </div>

      {/* Actions */}
      <div className="loveshot-actions">
        <div className="action-buttons">
          <button
            className={`action-btn ${isLiked ? 'liked' : ''}`}
            onClick={handleLike}
          >
            <FaHeart />
            <span>{loveshot.likes_count || 0}</span>
          </button>
          <button
            className="action-btn"
            onClick={() => setShowComments(!showComments)}
          >
            <FaComment />
            <span>{loveshot.comments_count || 0}</span>
          </button>
          <button className="action-btn">
            <FaShare />
          </button>
        </div>
        <button
          className={`action-btn save-btn ${isSaved ? 'saved' : ''}`}
          onClick={handleSave}
        >
          <FaBookmark />
        </button>
      </div>

      {/* Content */}
      {loveshot.caption && (
        <div className="loveshot-content">
          <p>{loveshot.caption}</p>
        </div>
      )}

      {/* Comments Section */}
      {showComments && (
        <div className="comments-section">
          <div className="comments-list">
            {loveshot.comments && loveshot.comments.length > 0 ? (
              loveshot.comments.map(commentItem => {
                if (!commentItem) return null;
                return (
                  <div key={commentItem.id} className="comment">
                    <img
                      src={commentItem.user?.profile?.photo_url || '/default-avatar.svg'}
                      alt={commentItem.user?.username || 'User'}
                      className="comment-avatar"

                    />
                    <div className="comment-content">
                      <strong>{commentItem.user?.username || 'Utilisateur'}</strong>
                      <p>{commentItem.content || commentItem.text}</p>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="no-comments">Aucun commentaire pour le moment</p>
            )}
          </div>

          <form onSubmit={handleComment} className="comment-form">
            <div className="comment-input-wrapper">
              <button
                type="button"
                className="emoji-btn-small"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                title="Ajouter un emoji"
              >
                <FaSmile />
              </button>
              <input
                type="text"
                placeholder="Ajouter un commentaire..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="comment-input"
                disabled={isCommentLoading}
              />
              {showEmojiPicker && (
                <div className="comment-emoji-picker">
                  <div className="emoji-grid-small">
                    {emojis.map((emoji, index) => (
                      <button
                        key={index}
                        type="button"
                        className="emoji-item-small"
                        onClick={() => {
                          setComment((prevComment) => prevComment + emoji);
                        }}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button type="submit" className="comment-submit-btn" disabled={isCommentLoading || !comment.trim()}>
              {isCommentLoading ? '...' : 'Publier'}
            </button>
          </form>
        </div>
      )}

      {/* Timestamp */}
      <div className="loveshot-timestamp">
        {new Date(loveshot.created_at).toLocaleDateString('fr-FR')}
      </div>

      {/* Full Image Modal */}
      {showFullImage && (
        <div className="full-image-overlay" onClick={handleCloseFullImage}>
          <div className="full-image-container" onClick={(e) => e.stopPropagation()}>
            <button className="close-full-image-btn" onClick={handleCloseFullImage}>
              <FaTimes />
            </button>
            <img 
              src={loveshot.media_url} 
              alt="LoveShot Full Size" 
              className="full-image"
              onError={(e) => {
                if (!e.target.src.includes('data:image')) {
                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22400%22 height=%22400%22 viewBox=%220 0 400 400%22%3E%3Crect width=%22400%22 height=%22400%22 fill=%22%23f5f5f5%22/%3E%3Ccircle cx=%22200%22 cy=%22150%22 r=%2250%22 fill=%22%23e0e0e0%22/%3E%3Cpath d=%22M 100 300 L 150 200 L 250 280 L 300 220 L 350 300 L 350 400 L 50 400 Z%22 fill=%22%23e0e0e0%22/%3E%3Ctext x=%22200%22 y=%22380%22 font-size=%2214%22 fill=%22%23999%22 text-anchor=%22middle%22%3ENo image%3C/text%3E%3C/svg%3E';
                }
              }}
            />
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="delete-confirm-overlay" onClick={handleCancelDelete}>
          <div 
            className="delete-confirm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="delete-confirm-header">
              <h3>Supprimer ce LoveShot</h3>
            </div>
            <div className="delete-confirm-body">
              <p>Êtes-vous sûr de vouloir supprimer ce LoveShot? Cette action est irréversible.</p>
            </div>
            <div className="delete-confirm-actions">
              <button 
                className="btn-cancel"
                onClick={handleCancelDelete}
                disabled={isDeleting}
              >
                Annuler
              </button>
              <button 
                className="btn-delete"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoveShotCard;
